import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { LinkContainer } from "react-router-bootstrap";
import { ChevronLeft, ChevronRight } from "react-bootstrap-icons";
import { getUpdateNote, getUpdateNotes } from "../services/UpdateNotesService";
import { LoadingSpinner } from "../components/LoadingSpinner";
import './Updates.css';

const PAGE_SIZE = 10;

// ── Module-level stable refs ──────────────────────────────────────────────────

function formatDate(dateStr, opts) {
  return new Date(dateStr).toLocaleDateString('en-US', opts);
}

const notesFilter = (n, q) =>
  n.Title.toLowerCase().includes(q) ||
  formatDate(n.Date, { day: '2-digit', month: 'short', year: 'numeric' }).toLowerCase().includes(q);

const SORT_OPTIONS = [
  { value: 'date-desc',  label: 'Newest first' },
  { value: 'date-asc',   label: 'Oldest first' },
  { value: 'title-asc',  label: 'Title A–Z'    },
  { value: 'title-desc', label: 'Title Z–A'    },
];

function sortNotes(notes, sortValue) {
  const [key, dir] = sortValue.split('-');
  const getter = key === 'date'
    ? n => new Date(n.Date).getTime()
    : n => n.Title.toLowerCase();
  return [...notes].sort((a, b) => {
    const av = getter(a), bv = getter(b);
    if (av < bv) return dir === 'asc' ? -1 : 1;
    if (av > bv) return dir === 'asc' ? 1 : -1;
    return 0;
  });
}

// ── Hook ──────────────────────────────────────────────────────────────────────

function useUpdateListControls(notes) {
  const [query,      setQuery]      = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [sort,       setSort]       = useState('date-desc');
  const [page,       setPage]       = useState(1);

  const yearFiltered = useMemo(() => {
    if (!yearFilter) return notes;
    const y = parseInt(yearFilter, 10);
    return notes.filter(n => new Date(n.Date).getFullYear() === y);
  }, [notes, yearFilter]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? yearFiltered.filter(n => notesFilter(n, q)) : yearFiltered;
  }, [yearFiltered, query]);

  const sorted = useMemo(() => sortNotes(filtered, sort), [filtered, sort]);

  useEffect(() => { setPage(1); }, [query, sort, yearFilter]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage  = Math.min(page, pageCount);
  const start     = (safePage - 1) * PAGE_SIZE;

  return {
    query, setQuery,
    yearFilter, setYearFilter,
    sort, setSort,
    page: safePage, setPage,
    pageCount,
    pageItems:     sorted.slice(start, start + PAGE_SIZE),
    totalFiltered: sorted.length,
    total:         notes.length,
  };
}

// ── Sub-components ────────────────────────────────────────────────────────────

function UpdatesPagination({ page, pageCount, onPage }) {
  if (pageCount <= 1) return null;
  return (
    <div className="updates-pagination">
      <button className="updates-page-btn" onClick={() => onPage(1)}          disabled={page === 1}>«</button>
      <button className="updates-page-btn" onClick={() => onPage(page - 1)}   disabled={page === 1}>‹</button>
      <span className="updates-page-info">Page {page} of {pageCount}</span>
      <button className="updates-page-btn" onClick={() => onPage(page + 1)}   disabled={page === pageCount}>›</button>
      <button className="updates-page-btn" onClick={() => onPage(pageCount)}  disabled={page === pageCount}>»</button>
    </div>
  );
}

function UpdatesList({ notes }) {
  const {
    query, setQuery,
    yearFilter, setYearFilter,
    sort, setSort,
    page, setPage,
    pageCount,
    pageItems,
    totalFiltered,
    total,
  } = useUpdateListControls(notes);

  const availableYears = useMemo(() => {
    const years = new Set(notes.map(n => new Date(n.Date).getFullYear()));
    return [...years].sort((a, b) => b - a);
  }, [notes]);

  const isFiltered = totalFiltered !== total;

  return (
    <div className="updates-list-page">
      <div className="updates-list-header">
        <h1>Updates</h1>
      </div>

      <div className="updates-controls">
        <input
          type="text"
          className="updates-search-input"
          placeholder="Search updates…"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        <select
          className="updates-filter-select"
          value={yearFilter}
          onChange={e => setYearFilter(e.target.value)}
          aria-label="Filter by year"
        >
          <option value="">All years</option>
          {availableYears.map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <select
          className="updates-sort-select"
          value={sort}
          onChange={e => setSort(e.target.value)}
          aria-label="Sort order"
        >
          {SORT_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <span className="updates-result-count">
          {isFiltered ? `${totalFiltered} of ${total}` : total}
          {' '}update{total !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="updates-list">
        {pageItems.length === 0 ? (
          <div className="updates-empty">No updates match your search.</div>
        ) : pageItems.map(note => (
          <LinkContainer
            key={note.ID}
            exact
            to={`/updates/${note.ID}/${note.Title.replaceAll(' ', '-')}`}
          >
            <div className="updates-list-item">
              <span className="updates-list-date">
                {formatDate(note.Date, { day: '2-digit', month: 'short', year: 'numeric' })}
              </span>
              <span className="updates-list-divider" aria-hidden="true" />
              <span className="updates-list-title">{note.Title}</span>
              <ChevronRight className="updates-list-arrow" />
            </div>
          </LinkContainer>
        ))}
      </div>

      <div className="updates-list-footer">
        <UpdatesPagination page={page} pageCount={pageCount} onPage={setPage} />
        {!isFiltered && (
          <p className="updates-total-count">{total} update{total !== 1 ? 's' : ''} total</p>
        )}
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function Updates() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);

    async function load() {
      try {
        const result = id
          ? (await getUpdateNote(id))[0]
          : await getUpdateNotes();
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) return <LoadingSpinner />;
  if (error)   return <p className="updates-error">{error}</p>;
  if (!data)   return null;

  // Guard against stale data while id transition is in-flight
  if (id && Array.isArray(data))   return null;
  if (!id && !Array.isArray(data)) return null;

  if (id) {
    return (
      <article className="updates-article">
        <Link className="updates-back-link" to="/updates">
          <ChevronLeft /> Back to Updates
        </Link>
        <div className="updates-article-header">
          <p className="updates-article-meta">
            {formatDate(data.Date, { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
          <h1 className="updates-article-title">{data.Title}</h1>
        </div>
        <div className="updates-article-body">
          <div
            className="updates-article-content"
            dangerouslySetInnerHTML={{ __html: data.Content }}
          />
        </div>
        <div className="updates-article-footer">
          <Link className="updates-back-link updates-back-link--centered" to="/updates">
            <ChevronLeft /> View All Updates
          </Link>
        </div>
      </article>
    );
  }

  return <UpdatesList notes={data} />;
}
